import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material'
import ScheduleIcon from '@mui/icons-material/Schedule'
import { useAuth } from 'hooks/auth/useAuth'
import { useTimesheet, useClockReminderSettings } from 'hooks/timesheet'
import { DateTime } from 'luxon'

const TIMEZONE = 'America/New_York'
const WORK_START_HOUR = 9
const WORK_END_HOUR = 17
const SHOW_AFTER_MS = 60 * 1000 // Show reminder after 1 minute on the app
const SESSION_STORAGE_KEY = 'clockInReminderShown'

function isWeekday(): boolean {
  const now = DateTime.now().setZone(TIMEZONE)
  return now.weekday >= 1 && now.weekday <= 5
}

function isWithinWorkHours(): boolean {
  const now = DateTime.now().setZone(TIMEZONE)
  return now.hour >= WORK_START_HOUR && now.hour < WORK_END_HOUR
}

export const ClockInReminder: React.FC = () => {
  const [open, setOpen] = useState(false)
  const [checkPassed, setCheckPassed] = useState(false)
  const { userInfo } = useAuth()
  const { isTimerActive } = useTimesheet()
  const { isPopupEnabledForUser, isLoading: isLoadingReminderSettings } = useClockReminderSettings()

  const shouldShowReminder =
    Boolean(userInfo) &&
    !userInfo?.isOwner &&
    userInfo?.role !== 'client' &&
    !isLoadingReminderSettings &&
    isPopupEnabledForUser(userInfo?.id)

  useEffect(() => {
    if (!shouldShowReminder) return
    if (isTimerActive) return
    if (!isWeekday() || !isWithinWorkHours()) return
    if (sessionStorage.getItem(SESSION_STORAGE_KEY)) return

    const timer = setTimeout(() => {
      setCheckPassed(true)
    }, SHOW_AFTER_MS)

    return () => clearTimeout(timer)
  }, [shouldShowReminder, isTimerActive])

  useEffect(() => {
    if (isTimerActive) {
      setOpen(false)
      return
    }
    if (!checkPassed || !shouldShowReminder) return
    if (!isWeekday() || !isWithinWorkHours()) return
    if (sessionStorage.getItem(SESSION_STORAGE_KEY)) return

    setOpen(true)
  }, [checkPassed, shouldShowReminder, isTimerActive])

  const handleDismiss = () => {
    sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
    setOpen(false)
  }

  return (
    <Dialog
      open={open}
      onClose={handleDismiss}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          color: 'primary.main',
        }}
      >
        <ScheduleIcon color="primary" />
        Don't forget to clock in
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" paragraph>
          You're not currently clocked in. Remember to clock in when you start your shift so your time is tracked
          accurately.
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={handleDismiss} variant="contained" color="primary">
          Got it
        </Button>
      </DialogActions>
    </Dialog>
  )
}
